// Hypothesis card — pure helpers (no AI, no network, rule-based).
//
// The six fields are GENERATED from a tiny conversation, never asked for as a
// blank form. This module: classifies the mechanism family, drafts a card from
// the user's idea + answers, and gates "Build" via a readiness check.
// See docs/track-record/HYPOTHESIS-INTAKE-UX-SPEC.md.

import type { HypothesisCard, HypothesisReadiness, MechanismFamily } from '../../types/validation';

// Ordered: more specific families first so e.g. "funding" → carry, not trend.
const FAMILY_KEYWORDS: ReadonlyArray<readonly [MechanismFamily, readonly string[]]> = [
  ['carry_cashflow', ['funding', 'carry', 'yield', 'dividend', 'interest', 'roll yield', 'cashflow']],
  ['event_driven', ['earnings', 'announcement', 'unlock', 'event', 'news', 'release', 'fomc', 'fed ']],
  ['microstructure', ['liquidation', 'order flow', 'orderflow', 'spike', 'wick', 'bid', 'ask', 'spread', 'tick']],
  ['cross_sectional', ['relative strength', 'cross-sectional', 'cross sectional', 'rank', 'strongest', 'vs peers']],
  ['mean_reversion', ['oversold', 'overbought', 'reversion', 'reversal', 'rsi', 'bounce', 'fade', 'mean revert']],
  ['seasonality', ['weekend', 'monday', 'seasonal', 'time of day', 'day of week', 'calendar', 'turn of month']],
  ['structural_arb', ['arbitrage', 'mispricing', 'basis trade']],
  ['flow_sentiment', ['sentiment', 'positioning', 'retail flow', 'whale']],
  ['trend_momentum', ['trend', 'momentum', 'breakout', 'moving average', 'donchian', 'follow the']],
];

export function classifyMechanismFamily(text: string): MechanismFamily {
  const t = (text || '').toLowerCase();
  for (const [fam, kws] of FAMILY_KEYWORDS) if (kws.some(k => t.includes(k))) return fam;
  return 'unknown';
}

// ── Adaptive intake: detect what the idea already answers ──────────────────
// So we DON'T re-ask for a mechanism or an edge the user already stated.
// Biased toward precision: when unsure we report "not present" and simply ask
// the follow-up (one extra question is harmless; fabricating a mechanism is not).

const CAUSAL_MARKERS = [
  'because', 'since ', 'due to', 'driven by', 'caused by', 'as a result', 'result of',
  'overreact', 'underreact', 'overshoot', 'mispric', 'inefficien', 'oversold', 'overbought',
  'mean revert', 'revert to', 'forced to', 'get paid', 'gets paid', 'paid to', 'pays ', 'compensate',
];

const MAGNITUDE_RE = /\b\d+(?:\.\d+)?\s*(?:%|percent|bps?|basis points?|pips?)/i;
const PER_PERIOD_RE = /\b\d+(?:\.\d+)?\s*(?:per|a|\/)\s*(?:day|week|month|year|trade|session)\b/i;

export interface IdeaSignals {
  hasCause: boolean;   // the idea already states a mechanism (the "why")
  hasEdge: boolean;    // the idea already states a direction/size
  causeText: string;   // suggested mechanism when hasCause
  edgeText: string;    // suggested edge when hasEdge
}

export function extractSignals(ideaText: string): IdeaSignals {
  const raw = (ideaText || '').trim();
  const t = raw.toLowerCase();
  const hasCause = CAUSAL_MARKERS.some((m) => t.includes(m));
  const mag = raw.match(MAGNITUDE_RE) || raw.match(PER_PERIOD_RE);
  const hasEdge = !!mag;
  const dir = /\b(long|short)\b/i.exec(raw)?.[1];
  const edgeText = hasEdge ? `${dir ? dir.toLowerCase() + '; ' : ''}${mag![0].trim()}` : '';
  // When the idea already explains itself, use it as the mechanism line; the
  // user can refine it in Advanced.
  const causeText = hasCause ? raw : '';
  return { hasCause, hasEdge, causeText, edgeText };
}

export const DEFAULT_VALIDATION_PLAN =
  'Matched-random benchmark + out-of-sample split + realistic cost grid';

export const DEFAULT_FALSIFICATION =
  "If it doesn't beat random trades with the same costs, drop it";

// A cleaned, single-sentence restatement. Deliberately simple — the user edits
// it in Advanced view. No model call.
export function draftSentence(ideaText: string, mechanism: string): string {
  const idea = (ideaText || '').trim().replace(/\s+/g, ' ').replace(/[.]+$/, '');
  if (!idea) return '';
  const because = mechanism.trim() ? `, because ${mechanism.trim().replace(/[.]+$/, '')}` : '';
  const lead = /^[a-z]/.test(idea) ? idea[0].toUpperCase() + idea.slice(1) : idea;
  return `${lead}${because}.`;
}

export interface DraftInput {
  id: string;
  ideaText: string;
  cause?: string;     // "why would the market pay you?"
  edge?: string;      // "which way, how big?"
  createdAt: string;  // ISO (caller supplies — keeps this pure/deterministic)
}

export function buildDraftCard(input: DraftInput): HypothesisCard {
  const mechanism = (input.cause || '').trim();
  const expectedEdge = (input.edge || '').trim();
  return {
    id: input.id,
    ideaText: (input.ideaText || '').trim(),
    hypothesisSentence: draftSentence(input.ideaText, mechanism),
    mechanism,
    expectedEdge,
    requiredData: '',
    costToEdgeNote: '',
    falsification: DEFAULT_FALSIFICATION,
    validationPlan: DEFAULT_VALIDATION_PLAN,
    mechanismFamily: classifyMechanismFamily(`${input.ideaText} ${mechanism}`),
    createdAt: input.createdAt,
  };
}

// Readiness gate (§7 of the intake spec, simplified for this slice).
// Proceed-to-build requires: a hypothesis sentence, a stated mechanism (the
// "why"), an expected edge, and a falsification condition. Missing items are
// phrased as single assumptions and surfaced ONE at a time by the UI —
// never "you failed".
export function evaluateReadiness(card: HypothesisCard): HypothesisReadiness {
  const missing: string[] = [];
  if (!card.hypothesisSentence.trim()) missing.push('a one-line version of the idea');
  if (!card.mechanism.trim()) missing.push('why this should work (the mechanism)');
  if (!card.expectedEdge.trim()) missing.push('which way it goes and roughly how big');
  if (!card.falsification.trim()) missing.push('what would prove it wrong');
  return { ready: missing.length === 0, missing };
}

export function mechanismFamilyLabel(f: MechanismFamily): string {
  switch (f) {
    case 'trend_momentum': return 'Trend / Momentum';
    case 'mean_reversion': return 'Mean-Reversion';
    case 'carry_cashflow': return 'Carry / Cashflow';
    case 'event_driven': return 'Event-Driven';
    case 'microstructure': return 'Microstructure';
    case 'cross_sectional': return 'Cross-sectional';
    case 'flow_sentiment': return 'Flow / Sentiment';
    case 'seasonality': return 'Seasonality / Calendar';
    case 'structural_arb': return 'Structural / Arb';
    default: return 'Uncategorized';
  }
}
